import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Modal from "../../components/common/Modal";
import {
  TProductPriceHistory,
  TProductPriceHistoryForm,
  ProductPriceHistoryTable,
  ProductPriceHistoryForm,
  ProductPriceHistoryHeader,
} from "../../features/productPriceHistories";
import { useIsAuth } from "../../utils/useAuth";
import { FormikHelpers, useFormik } from "formik";
import { usePagination } from "../../components/common/data-table/hooks/usePagination";
import { useProductPriceHistories } from "../../features/productPriceHistories/hooks/useProductPriceHistories";
import { faCheck, faEdit, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  useProductPriceHistoryStatusToggle,
  useSaveProductPriceHistory,
  productPriceHistorySchema,
} from "../../features/productPriceHistories";

const ProductPriceHistoryPage = () => {
  const { productId = "" } = useParams();
  const { userData } = useIsAuth();
  const [showModal, setShowModal] = useState<boolean>(false);

  const formDataInit: TProductPriceHistoryForm = useMemo(() => {
    return {
      productId,
      price: 0,
      cost: 0,
      effectiveFrom: new Date(),
      effectiveTo: null,
      createdBy: userData?.userId || "",
      active: true,
    };
  }, [userData?.userId]);

  // Hooks
  const { pagination, handlePageChange } = usePagination();
  // Fetch product price history list
  const { productPriceHistories, paginationData } = useProductPriceHistories({
    filter: {
      productId,
    },
    pagination,
  });

  // Mutation to save product price history
  const { handleSave } = useSaveProductPriceHistory();

  const { handleEnable, handleDisable } = useProductPriceHistoryStatusToggle();

  const formik = useFormik<TProductPriceHistoryForm>({
    initialValues: formDataInit,
    validationSchema: productPriceHistorySchema,
    onSubmit: (
      values: TProductPriceHistoryForm,
      formikHelpers: FormikHelpers<TProductPriceHistoryForm>,
    ) => {
      handleSave(values);
      formikHelpers.setSubmitting(false); // You can perform additional actions here if needed
      formikHelpers.resetForm();
      handleClose();
    },
  });

  const handleOpenNewPriceHistoryModal = () => {
    formik.resetForm();
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    formik.resetForm();
  };

  const handleEdit = (productPrice: TProductPriceHistory) => {
    setShowModal(true);
    formik.setValues(productPrice);
  };

  return (
    <div>
      <ProductPriceHistoryHeader
        onNewPriceHistory={handleOpenNewPriceHistoryModal}
      />
      <ProductPriceHistoryTable
        data={productPriceHistories} // TODO: add data
        isLoading={false} // TODO: add loading state
        pagination={{
          currentPage: paginationData?.currentPage ?? 1,
          totalPages: paginationData?.totalPages ?? 0,
          pageSize: paginationData?.pageSize ?? 10,
          total: paginationData?.total ?? 0,
          onPageChange: handlePageChange,
        }}
        extraActions={[
          {
            title: "Editar",
            icon: faEdit,
            actionKey: "edit",
            onClick: (row) => handleEdit(row),
          },
          {
            title: "Deshabilitar",
            icon: faXmark,
            actionKey: "disable",
            color: "secondary",
            onClick: (row) => handleDisable(row._id),
            shouldShow: (row) => row.active,
          },
          {
            title: "Habilitar",
            icon: faCheck,
            actionKey: "enable",
            color: "success",
            onClick: (row) => handleEnable(row._id),
            shouldShow: (row) => !row.active,
          },
        ]}
      />
      <Modal show={showModal} close={handleClose} submit={formik.handleSubmit}>
        <ProductPriceHistoryForm formik={formik} />
      </Modal>
    </div>
  );
};

export default ProductPriceHistoryPage;
